/**
 * @castinflow/shared — API ve web arasında paylaşılan sözleşmeler.
 * Plan §5: hata zarfı {error:{code,message,details?,requestId}}.
 */
import { z } from 'zod';
import { systemMailReadinessSchema } from './system-mail.js';
// Yan etki: Türkçe varsayılan doğrulama iletileri (API ve web aynı Zod örneğini kullanır; özel şema iletisi önceliklidir)
import './zod-tr.js';
// K-M7-50.5: zorunlu alan iletisinin tek yazımı (API, içe aktarma raporu, formlar ve arayüz aynı metni kullanır)
export { REQUIRED_FIELD_MESSAGE } from './zod-tr.js';
export const ERROR_CODES = [
    'BAD_REQUEST',
    'UNAUTHENTICATED',
    'FORBIDDEN',
    'PLAN_LIMIT',
    'NOT_FOUND',
    'CONFLICT',
    'APPROVAL_REQUIRED',
    'VALIDATION_FAILED',
    'RATE_LIMITED',
    'STORAGE_FULL',
    'NOT_READY',
    /** Sorgu maliyet sınırı (statement_timeout) aşıldı; istemci sorguyu daraltmalı (WP 2.11, 4xx) */
    'QUERY_TIMEOUT',
    /** v3.1 S9: gelen aday ucu — istek gövdesi boyut sınırını aştı (413) */
    'PAYLOAD_TOO_LARGE',
    /**
     * v3.1 B3 (ADR-0015 § 5.1): çalışma alanı platform operatörünce askıya alındı — oturum, API anahtarı, destek oturumu
     * reddedilir; S9 gelen aday ucu da askıda olayı kabul etmez ve kaydetmez (423). Tek kanonik kod.
     */
    'TENANT_SUSPENDED',
    /** v3.1 B3 (ADR-0015 § 5.3): deneme süresi doldu — yazma kapalı; okuma, arama, dışa aktarma açık */
    'TRIAL_EXPIRED',
    'INTERNAL',
];
export const HTTP_STATUS_BY_CODE = {
    BAD_REQUEST: 400,
    UNAUTHENTICATED: 401,
    FORBIDDEN: 403,
    PLAN_LIMIT: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    APPROVAL_REQUIRED: 409,
    VALIDATION_FAILED: 422,
    RATE_LIMITED: 429,
    STORAGE_FULL: 507,
    NOT_READY: 503,
    QUERY_TIMEOUT: 422,
    PAYLOAD_TOO_LARGE: 413,
    TENANT_SUSPENDED: 423,
    TRIAL_EXPIRED: 403,
    INTERNAL: 500,
};
export const errorEnvelopeSchema = z.object({
    error: z.object({
        code: z.enum(ERROR_CODES),
        message: z.string(),
        details: z.unknown().optional(),
        requestId: z.string(),
    }),
});
export const healthResponseSchema = z.object({
    status: z.literal('ok'),
    service: z.string(),
    version: z.string(),
    uptimeSeconds: z.number(),
});
export const readyCheckSchema = z.object({
    name: z.string(),
    ok: z.boolean(),
    detail: z.string().optional(),
    durationMs: z.number(),
});
export const readyResponseSchema = z.object({
    status: z.enum(['ready', 'not_ready']),
    checks: z.array(readyCheckSchema),
    schemaVersion: z.string().nullable(),
    expectedSchemaVersion: z.string().nullable(),
    /**
     * Şema uyum sözleşmesi (geri dönüş güvencesi): bu alanı taşıyan sürümler kendilerinden yeni migration'ları geri-uyum
     * bildirimiyle tolere edebilir. Alan yoksa sürüm tam eşitlik ister (dağıtım ön kontrolü geri dönüş sınıfını buna göre
     * belirler). Sözleşme 2: `known` = bu sürümün bildiği migration kümesinin özeti (sayı + kimliklerin SHA-256'sı); ön kontrol
     * hedefin "ara migration" getirip getirmediğini bununla ölçer.
     */
    schemaContract: z
        .object({
        version: z.number().int(),
        tolerated: z.array(z.string()),
        known: z.object({ count: z.number().int(), digest: z.string() }).optional(),
        /**
         * Şema durumu (12 Ekim 2026; trafiğe alma kararı için): `checks` içindeki `schema` denetimi düştüğünde NEDEN düştüğünü
         * ayırır. `compatible` uygulanmış küme okundu ve bu sürümle uyumlu · `incompatible` uygulanmış küme OKUNDU ve bu sürüm
         * onunla uyum beyan etmiyor (kesin karar) · `unknown` değerlendirilemedi (veritabanına ulaşılamadı, küme okunamadı) —
         * "bilinmiyor", "uyumsuz" DEĞİLDİR. Eski sürümlerde alan yoktur.
         */
        state: z.enum(['compatible', 'incompatible', 'unknown']).optional(),
    })
        .optional(),
    /**
     * Dış etki koruması (M7 T3) — BİLGİ alanıdır: `held` iken `status` yine `ready` kalır (dağıtım hazır denetimi etkilenmez).
     * `reason` kapalı kümeden bir koddur; ortam kimliği ve sır içermez. Eski sürümlerde alan yoktur.
     * `source: 'forced'` (M7 ikinci dilim) yalnız ZORUNLU beklemede bulunur (`EXTERNAL_EFFECTS_FORCED_HOLD`): o zaman `reason`
     * sunucunun verdiği kısa neden kodudur (biçim `FORCED_HOLD_CODE_RE`) ve bekleme veritabanındaki durumdan bağımsızdır.
     */
    externalEffects: z
        .object({
        status: z.enum(['open', 'held']),
        reason: z.string().nullable(),
        since: z.string().nullable(),
        source: z.enum(['forced']).optional(),
    })
        .optional(),
    /**
     * Nesne deposu (M7 ikinci dilim) — yazma kipi ve erişilebilirlik. `mode`: `STORAGE_WRITE_MODE`. `status`: `ok` erişilebilir ·
     * `unreachable` yapılandırılmış ama yoklama başarısız (ağ yok, kimlik reddedildi, zaman aşımı) · `unconfigured` uç nokta,
     * kova ya da kimlik bilgisi verilmemiş. `reachable` = `status === 'ok'`. Depo ERİŞİLEMEZKEN sürüm "hazır" görünmez:
     * yapılandırılmış depoda `checks` içindeki `storage` denetimi düşer; üretim kipinde yapılandırılmamış depo da aynı denetimi
     * düşürür (`status: not_ready`, ayrıntı "depolama erişilemez: …"). Geliştirme/testte yapılandırılmamış depo `status`'u
     * etkilemez (denetim listesine girmez). Eski sürümlerde alan yoktur.
     */
    storage: z
        .object({
        mode: z.enum(['read-write', 'read-only']),
        status: z.enum(['ok', 'unreachable', 'unconfigured']),
        reachable: z.boolean(),
    })
        .optional(),
    /**
     * M7 T2 — sistem postası (davet, parola sıfırlama) yapılandırma durumu. BİLGİ alanıdır: `status` ve `checks` bundan
     * etkilenmez. Alan yoksa sürüm bu bilgiyi yayımlamıyordur (önceki sürüm).
     */
    systemMail: systemMailReadinessSchema.optional(),
});
export * from './slug.js';
export * from './tenant.js';
export * from './auth.js';
export * from './org.js';
export * from './roles.js';
export * from './capability-approval.js';
export * from './system-mail.js';
export * from './metadata.js';
export * from './records.js';
export * from './formula.js';
export * from './custom-fields.js';
export * from './field-access.js';
export * from './list-query.js';
export * from './record-extras.js';
export * from './form-signatures.js';
export * from './record-view.js';
export * from './crm.js';
export * from './assignment.js';
export * from './user-handover.js';
export * from './decimal.js';
export * from './formula-decimal.js';
export * from './products.js';
export * from './search.js';
export * from './search-document.js';
export * from './transfer.js';
export * from './pricing.js';
export * from './sales.js';
export * from './approval.js';
export * from './leads.js';
export * from './identity.js';
export * from './feed.js';
export * from './contracts.js';
export * from './activities.js';
export * from './tickets.js';
export * from './ticket-sla.js';
export * from './support-queues.js';
export * from './contract-docx.js';
export * from './work-calendar.js';
export * from './demo.js';
export * from './campaigns.js';
export * from './proposal-templates.js';
export * from './public-form-channel.js';
export * from './public-forms.js';
export * from './ticket-forms.js';
export * from './proposal-links.js';
export * from './sales-views.js';
export * from './order-contract-tracking.js';
export * from './purchasing.js';
export * from './procurement.js';
export * from './lead-sequences.js';
export * from './dashboards.js';
export * from './integrations.js';
export * from './automation.js';
export * from './automation-schedule.js';
export * from './today.js';
export * from './today-insights.js';
export * from './daily-work.js';
export * from './marketing.js';
export * from './marketing-audience.js';
export * from './forms.js';
export * from './gamification.js';
export * from './email.js';
export * from './inbound-email.js';
export * from './plans.js';
export * from './modules.js';
export * from './delivery.js';
export * from './portal.js';
export * from './delivery-context.js';
export * from './ticket-context.js';
export * from './external-source-contract.js';
export * from './job-quarantine.js';
export * from './tenant-effects.js';
export * from './data-lifecycle.js';
export * from './workspace-export.js';
export * from './account-security.js';
export * from './ip-allowlist.js';
export * from './notification-preferences.js';
export * from './ui-settings.js';
export * from './integration-catalog.js';
export * from './ops-console.js';
export * from './provider-connections.js';
export * from './provider-status.js';
export * from './conversations.js';
export * from './consent.js';
export * from './usage-metering.js';
export * from './tenant-lifecycle.js';
export * from './acquisition.js';
export * from './lead-events.js';
export * from './distribution.js';
export * from './meta-lead-ads.js';
export * from './ad-spend.js';
export * from './ad-change-requests.js';
export * from './ad-account-connect.js';
export * from './meta-permission-sets.js';
export * from './whatsapp-cloud.js';
export * from './connect-session.js';
export * from './google-ads-connect.js';
export * from './meta-page-connect.js';
export * from './whatsapp-signup.js';
export * from './agents.js';
export * from './communication-policy.js';
export * from './agent-context.js';
export * from './agent-usage.js';
export * from './record-origin.js';
export * from './sales-assessment.js';
export * from './research.js';
export * from './call-events.js';
export * from './assistant-name.js';
export * from './tenant-profile.js';
//# sourceMappingURL=index.js.map